/**
 * HolidayFormDialog Component
 * Dialog for creating/editing holidays
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import {
  Holiday,
  HolidayFormData,
  HolidayType,
  HOLIDAY_TYPE_LABELS,
  UF_OPTIONS,
  requiresState,
  requiresCity,
  requiresCompany,
} from '../../../types/holiday';

interface HolidayFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: HolidayFormData) => Promise<void>;
  initialData?: Holiday | null;
  companyId?: number;
}

const initialFormData: HolidayFormData = {
  date: '',
  name: '',
  type: HolidayType.NACIONAL,
  state: undefined,
  city: undefined,
  companyId: undefined,
  isRecurring: true,
};

const HolidayFormDialog: React.FC<HolidayFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  companyId,
}) => {
  const [formData, setFormData] = useState<HolidayFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!initialData;

  // Initialize form with initial data
  useEffect(() => {
    if (initialData) {
      setFormData({
        date: initialData.date,
        name: initialData.name,
        type: initialData.type,
        state: initialData.state,
        city: initialData.city,
        companyId: initialData.companyId,
        isRecurring: initialData.isRecurring,
      });
    } else {
      setFormData({
        ...initialFormData,
        companyId: companyId,
      });
    }
  }, [initialData, companyId]);

  const handleChange = (field: keyof HolidayFormData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear dependent fields when type changes
    if (field === 'type') {
      const newType = value as HolidayType;
      if (!requiresState(newType)) {
        setFormData(prev => ({ ...prev, state: undefined, city: undefined }));
      }
      if (!requiresCity(newType)) {
        setFormData(prev => ({ ...prev, city: undefined }));
      }
      if (!requiresCompany(newType)) {
        setFormData(prev => ({ ...prev, companyId: undefined }));
      } else if (companyId) {
        setFormData(prev => ({ ...prev, companyId }));
      }
    }
  };

  const validateForm = (): boolean => {
    if (!formData.date) {
      setError('Data é obrigatória');
      return false;
    }
    if (!formData.name) {
      setError('Nome é obrigatório');
      return false;
    }
    if (!formData.type) {
      setError('Tipo é obrigatório');
      return false;
    }
    if (requiresState(formData.type) && !formData.state) {
      setError('Estado é obrigatório para este tipo de feriado');
      return false;
    }
    if (requiresCity(formData.type) && !formData.city) {
      setError('Cidade é obrigatória para feriados municipais');
      return false;
    }
    if (requiresCompany(formData.type) && !formData.companyId) {
      setError('Empresa é obrigatória para feriados de empresa');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await onSubmit(formData);
      handleClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar feriado';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Feriado' : 'Novo Feriado'}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Data"
            type="date"
            value={formData.date}
            onChange={(e) => handleChange('date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />

          <TextField
            label="Nome do Feriado"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Ex: Natal, Páscoa, Aniversário da Cidade"
            fullWidth
            required
          />

          <FormControl fullWidth required>
            <InputLabel>Tipo de Feriado</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              label="Tipo de Feriado"
            >
              {Object.entries(HOLIDAY_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {requiresState(formData.type) && (
            <FormControl fullWidth required>
              <InputLabel>Estado (UF)</InputLabel>
              <Select
                value={formData.state ?? ''}
                onChange={(e) => handleChange('state', e.target.value)}
                label="Estado (UF)"
              >
                {UF_OPTIONS.map((uf) => (
                  <MenuItem key={uf} value={uf}>
                    {uf}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {requiresCity(formData.type) && (
            <TextField
              label="Cidade"
              value={formData.city ?? ''}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Ex: São Paulo, Rio de Janeiro"
              fullWidth
              required
            />
          )}

          {requiresCompany(formData.type) && (
            <Typography variant="body2" color="text.secondary">
              Este feriado será associado à empresa selecionada.
            </Typography>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isRecurring ?? true}
                onChange={(e) => handleChange('isRecurring', e.target.checked)}
              />
            }
            label="Feriado recorrente (repete todo ano)"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HolidayFormDialog;
